import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { createAuthenticatedEventSource, resolveAccessToken } from '../../../services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { parseAITranscriptResponse, parseExistingAIData, getInvalidCallMessage } from '../../../utils/aiResponseParser'
import logger from '../../../utils/logger'
import { LoadingSpinner } from '../../atoms'
import { DrawerCard, DrawerBadge } from '../../DrawerComponents'
import styles from '../CallDrawerModern.module.scss'

const isCallEligibleForAI = (callDate) => {
  if (!callDate) return false

  let date
  if (typeof callDate === 'string') {
    date = new Date(callDate)
  } else if (callDate instanceof Date) {
    date = callDate
  } else {
    return false
  }

  if (isNaN(date.getTime())) return false

  return date.getFullYear() >= 2025
}

export const AISummaryCard = ({ selectedRow }) => {
  const { t } = useTranslation()

  const [aiSummary, setAISummary] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)

  const eventSourceRef = useRef(null)
  const streamEpochRef = useRef(0)

  const getCallId = useCallback(() => {
    if (selectedRow?.id) return selectedRow.id
    if (selectedRow?.call_id) return selectedRow.call_id
    if (selectedRow?.[3]) return selectedRow[3]
    return null
  }, [selectedRow])

  const generateAISummary = useCallback(async () => {
    const callId = getCallId()
    if (!callId) {
      logger.error('No call ID available for AI processing')
      return
    }

    /* istanbul ignore next -- cleanup of previous EventSource before new one */
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    setIsLoading(true)

    const streamEpoch = streamEpochRef.current
    const accessToken = await resolveAccessToken()

    if (streamEpoch !== streamEpochRef.current) return

    eventSourceRef.current = createAuthenticatedEventSource(
      apiEndpoints.aiProcessCallStream,
      { callId },
      {
        onProgress: () => {},
        onComplete: (data) => {
          if (data) {
            const { summary, isValid } = parseAITranscriptResponse(data)
            if (isValid && summary && typeof summary === 'string') {
              setAISummary(summary)
            } else if (!isValid) {
              setAISummary(getInvalidCallMessage())
            } else {
              setAISummary(getInvalidCallMessage())
            }
          }
          setIsLoading(false)
          setIsInitializing(false)
        },
        onError: () => {
          setIsLoading(false)
          setIsInitializing(false)
        }
      },
      accessToken
    )
  }, [getCallId])

  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset when selectedRow changes, also cleans up EventSource ref */
  useEffect(() => {
    streamEpochRef.current += 1
    /* istanbul ignore next -- EventSource ref cleanup on row change */
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    setHasCheckedEligibility(false)
    setAISummary(null)
    setIsLoading(false)
    setIsInitializing(true)
  }, [selectedRow?.id, selectedRow?.call_id])
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    return () => {
      streamEpochRef.current += 1
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  /* eslint-disable react-hooks/set-state-in-effect -- conditional state updates based on eligibility check */
  useEffect(() => {
    if (selectedRow && !hasCheckedEligibility) {
      const dateValue = selectedRow[0] || selectedRow.call_date || selectedRow.callDate
      const eligible = isCallEligibleForAI(dateValue)

      if (!eligible) {
        setHasCheckedEligibility(true)
        setIsInitializing(false)
        return
      }

      if (selectedRow.ai_summary) {
        try {
          const { summary, isValid } = parseExistingAIData(selectedRow, 'ai_summary')
          if (isValid && summary && typeof summary === 'string') {
            setAISummary(summary)
          } else if (!isValid) {
            setAISummary(getInvalidCallMessage())
          }
          setIsInitializing(false)
        } catch (e) {
          generateAISummary()
        }
      } else {
        generateAISummary()
      }
      setHasCheckedEligibility(true)
    }
  }, [selectedRow, hasCheckedEligibility, generateAISummary])
  /* eslint-enable react-hooks/set-state-in-effect */

  const renderBadge = () => {
    if (isLoading) {
      return (
        <DrawerBadge variant="info">
          <LoadingSpinner size={12} />
          Generating...
        </DrawerBadge>
      )
    }
    return null
  }

  return (
    <DrawerCard
      title="AI Summary"
      icon="brain"
      badge={renderBadge()}
      collapsible
      defaultExpanded
      fullWidth
    >
      <Box>
        {isLoading ? (
          <Box className={styles.loadingIndicator}>
            <LoadingSpinner size={20} message={t('calls.drawer.summary.analyzing')} />
          </Box>
        ) : aiSummary ? (
          <Typography className={styles.summaryText}>
            {aiSummary}
          </Typography>
        ) : !isInitializing && hasCheckedEligibility ? (
          <Box className={styles.warningBox}>
            <Typography className={styles.warningTitle}>
              <FontAwesomeIcon icon="exclamation-triangle" />
              No AI Summary Available
            </Typography>
            <Typography className={styles.warningText}>
              This could be due to the call being abandoned, from before 2025, or having poor audio quality.
            </Typography>
          </Box>
        ) : null}
      </Box>
    </DrawerCard>
  )
}

AISummaryCard.propTypes = {
  selectedRow: PropTypes.object
}

export default AISummaryCard
